import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "../styles/theme";
import { supabase } from "../supabaseClient";

const ISSUE_TYPES = [
  "Account Issue",
  "Billing Question",
  "Technical Problem",
  "Verification Question",
  "Document Upload Issue",
  "Other",
];

export default function SupportPage() {
  const [user, setUser] = useState(null);
  const [accountType, setAccountType] = useState("unknown");
  const [form, setForm] = useState({ name: "", email: "", issueType: "", description: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Pre-fill from auth session
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      const u = session.user;
      setUser(u);
      setForm(f => ({ ...f, email: u.email || "" }));

      // Fetch profile name and account type
      const { data: account } = await supabase
        .from("user_accounts")
        .select("account_type")
        .eq("user_id", u.id)
        .maybeSingle();
      if (account) setAccountType(account.account_type || "unknown");

      const { data: techProfile } = await supabase
        .from("tech_profiles")
        .select("name")
        .eq("user_id", u.id)
        .maybeSingle();
      if (techProfile?.name) {
        setForm(f => ({ ...f, name: techProfile.name }));
        return;
      }

      const { data: empProfile } = await supabase
        .from("employer_profiles")
        .select("company_name")
        .eq("user_id", u.id)
        .maybeSingle();
      if (empProfile?.company_name) {
        setForm(f => ({ ...f, name: empProfile.company_name }));
      }
    })();
  }, []);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required.";
    if (!form.email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email address.";
    if (!form.issueType) errs.issueType = "Select an issue type.";
    if (!form.description.trim()) errs.description = "Description is required.";
    else if (form.description.trim().length < 20) errs.description = "Description must be at least 20 characters.";
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setSubmitError("");

    try {
      const { data, error } = await supabase.functions.invoke("send-support-email", {
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          issueType: form.issueType,
          description: form.description.trim(),
          accountType,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setSubmitted(true);
      setForm({ name: "", email: "", issueType: "", description: "" });
    } catch (err) {
      setSubmitError(err.message || "Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: undefined }));
  };

  const errorStyle = { color: "#f87171", fontSize: 12, marginTop: 4 };
  const fieldGap = { marginBottom: 20 };

  return (
    <div style={{
      fontFamily: "'Georgia','Times New Roman',serif",
      background: "#0d0f10", color: "#e8e4dc", minHeight: "100vh",
      padding: "40px 20px",
    }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <Link to="/" style={{ color: "#d97706", fontSize: 14, textDecoration: "none" }}>
          {"\u2190"} Back to UXOHire
        </Link>

        <h1 style={{ fontSize: 28, fontWeight: "bold", margin: "24px 0 8px", letterSpacing: "-0.02em" }}>
          Contact Support
        </h1>
        <p style={{ color: "#7a7570", fontSize: 15, lineHeight: 1.6, margin: "0 0 32px" }}>
          Having an issue? We're here to help. We typically respond within 1 business day.
        </p>

        {submitted ? (
          <div style={{
            background: "#0a2a1a", border: "1px solid #4ade80", borderRadius: 10,
            padding: "32px 28px", textAlign: "center",
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>{"\u2705"}</div>
            <h2 style={{ fontSize: 18, fontWeight: "bold", margin: "0 0 12px" }}>
              Your message has been sent!
            </h2>
            <p style={{ color: "#9a9490", fontSize: 14, lineHeight: 1.7, margin: 0 }}>
              We typically respond within 1 business day. Check your email for our reply.
            </p>
            <button
              style={{ ...styles.btnPrimary, marginTop: 24 }}
              onClick={() => setSubmitted(false)}
            >
              Send Another Message
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{
            background: "#111316", border: "1px solid #1e2022", borderRadius: 12,
            padding: "36px 40px",
          }}>
            {submitError && (
              <div style={{
                background: "#2a1010", border: "1px solid #f8717133", borderRadius: 8,
                padding: "12px 16px", color: "#f87171", fontSize: 13, marginBottom: 20,
              }}>
                {submitError}
              </div>
            )}

            <div style={fieldGap}>
              <label style={styles.label}>Name *</label>
              <input
                style={{ ...styles.input, borderColor: errors.name ? "#f87171" : "#444" }}
                type="text"
                value={form.name}
                onChange={e => handleChange("name", e.target.value)}
                placeholder="Your name"
              />
              {errors.name && <div style={errorStyle}>{errors.name}</div>}
            </div>

            <div style={fieldGap}>
              <label style={styles.label}>Email *</label>
              <input
                style={{ ...styles.input, borderColor: errors.email ? "#f87171" : "#444" }}
                type="email"
                value={form.email}
                onChange={e => handleChange("email", e.target.value)}
                placeholder="your@email.com"
              />
              {errors.email && <div style={errorStyle}>{errors.email}</div>}
            </div>

            <div style={fieldGap}>
              <label style={styles.label}>Issue Type *</label>
              <select
                style={{ ...styles.input, borderColor: errors.issueType ? "#f87171" : "#444", appearance: "auto" }}
                value={form.issueType}
                onChange={e => handleChange("issueType", e.target.value)}
              >
                <option value="">Select an issue type...</option>
                {ISSUE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              {errors.issueType && <div style={errorStyle}>{errors.issueType}</div>}
            </div>

            <div style={fieldGap}>
              <label style={styles.label}>Description *</label>
              <textarea
                style={{ ...styles.textarea, borderColor: errors.description ? "#f87171" : "#444", minHeight: 140 }}
                value={form.description}
                onChange={e => handleChange("description", e.target.value)}
                placeholder="Please describe your issue in detail..."
              />
              {errors.description && <div style={errorStyle}>{errors.description}</div>}
              <div style={{ color: "#5a5550", fontSize: 11, marginTop: 4 }}>
                {form.description.trim().length}/20 characters minimum
              </div>
            </div>

            <button
              type="submit"
              style={{ ...styles.btnPrimary, width: "100%", opacity: submitting ? 0.6 : 1 }}
              disabled={submitting}
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
